import React from 'react';
import { useParams } from 'react-router-dom';
import QRCode from 'qrcode';
import { ITableConfiguration } from '@ready/table.core';
import { TablesService } from '../../services/tablesService';
import LocationsService from '../../../locations/services/LocationsService';
import { useSearchParams } from '../../../hooks/useSearchParams';
import styles from './TableCodesPrintPage.module.scss';

interface PageParams {
  contextId: string;
  locationId: string;
}

interface TableCode {
  table: ITableConfiguration;
  qrDataUrl: string;
}

const QR_TARGET_HOST = 'https://readypay.co';

const TableCodesPrintPage = () => {
  const { contextId, locationId } = useParams<PageParams>();
  const { tableId } = useSearchParams();
  const [loading, setLoading] = React.useState(true);
  const [locationTitle, setLocationTitle] = React.useState<string>('');
  const [codes, setCodes] = React.useState<TableCode[]>([]);
  const [error, setError] = React.useState<string>('');

  React.useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const [tableConfigurations, location] = await Promise.all([
          TablesService.getTableConfigurations(contextId, locationId, '', 1),
          LocationsService.getClientLocationById(contextId, locationId),
        ]);

        // IPagedResponse's declared `results` field doesn't match the API's actual `items` field
        // (the existing table list reducer already relies on `items` too) — check both.
        const allTables: ITableConfiguration[] =
          (tableConfigurations as any).items ?? (tableConfigurations as any).results ?? [];
        const tables = allTables.filter((table) => !!table.targetCode && (!tableId || table.id === tableId));

        const withCodes = await Promise.all(
          tables.map(async (table) => ({
            table,
            qrDataUrl: await QRCode.toDataURL(`${QR_TARGET_HOST}/${table.targetCode}`, { margin: 1, width: 220 }),
          }))
        );

        if (!cancelled) {
          setCodes(withCodes);
          const name = (location as any)?.displayName || (location as any)?.name || locationId;
          setLocationTitle(name);
        }
      } catch (err) {
        if (!cancelled) {
          const status = err?.status ? `[${err.status}] ` : '';
          setError(`${status}${err?.message || 'Something went wrong loading table codes.'}`);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [contextId, locationId, tableId]);

  React.useEffect(() => {
    if (!loading && codes.length) {
      const id = setTimeout(() => window.print(), 200);
      return () => clearTimeout(id);
    }
  }, [loading, codes.length]);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1>Table QR Codes</h1>
        <div className={styles.headerMeta}>
          <span>Location: {locationTitle || locationId}</span>
          <span>Printed: {new Date().toLocaleString()}</span>
        </div>
        {loading ? <div className={styles.loading}>Loading codes…</div> : null}
      </div>

      {!loading && error && <div className={styles.empty}>Couldn&rsquo;t load table codes: {error}</div>}
      {!loading && !error && codes.length === 0 && (
        <div className={styles.empty}>No table codes to display.</div>
      )}

      <div className={styles.grid}>
        {codes.map(({ table, qrDataUrl }) => (
          <div key={table.id} className={styles.card}>
            <div className={styles.tableName}>{table.tableName ?? 'Table'}</div>
            <img className={styles.qrImage} src={qrDataUrl} alt={`QR code for ${table.tableName ?? 'table'}`} />
            <div className={styles.code}>{table.targetCode}</div>
            <a className={styles.downloadLink} href={qrDataUrl} download={`table-${table.targetCode}-qr.png`}>
              Download PNG
            </a>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TableCodesPrintPage;
